import { useEffect, useRef, useState, type PointerEvent } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { registerNativeBackDismiss } from "../lib/nativeNavigation";

export type LightboxImage = { src: string; alt: string };

export function ImageLightbox({ images, initialIndex, onClose }: { images: LightboxImage[]; initialIndex: number; onClose: () => void }) {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const touchStartX = useRef<number | null>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const safeIndex = Math.min(Math.max(activeIndex, 0), images.length - 1);
  const activeImage = images[safeIndex];
  const hasActiveImage = Boolean(activeImage);

  useEffect(() => {
    if (hasActiveImage) return registerNativeBackDismiss(() => onCloseRef.current());
  }, [hasActiveImage]);

  function move(direction: -1 | 1) {
    if (images.length < 2) return;
    setActiveIndex((current) => (current + direction + images.length) % images.length);
  }

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = "hidden";
    dialog.current?.querySelector<HTMLButtonElement>("button")?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onCloseRef.current();
      if (event.key === "ArrowLeft") move(-1);
      if (event.key === "ArrowRight") move(1);
      if (event.key === "Tab") {
        const buttons = dialog.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)");
        if (!buttons?.length) return;
        const first = buttons[0], last = buttons[buttons.length - 1];
        if (!dialog.current?.contains(document.activeElement) || (event.shiftKey ? document.activeElement === first : document.activeElement === last)) {
          event.preventDefault(); (event.shiftKey ? last : first).focus();
        }
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [images.length]);

  if (!activeImage) return null;

  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    touchStartX.current = event.pointerType === "touch" ? event.clientX : null;
  }

  function onPointerUp(event: PointerEvent<HTMLDivElement>) {
    if (touchStartX.current === null) return;
    const distance = event.clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(distance) > 48) move(distance > 0 ? -1 : 1);
  }

  return (
    <div ref={dialog} className="fixed inset-0 z-[80] grid place-items-center bg-black/90 p-3 sm:p-6" role="dialog" aria-modal="true" aria-label={activeImage.alt} onClick={onClose}>
      <button type="button" onClick={onClose} aria-label="Close image viewer" className="absolute right-3 top-3 z-10 grid h-11 w-11 place-items-center rounded-full bg-white/15 text-white hover:bg-white/25 sm:right-5 sm:top-5"><X className="h-6 w-6" /></button>
      <div className="relative flex h-full w-full touch-none items-center justify-center" onClick={(event) => event.stopPropagation()} onPointerDown={onPointerDown} onPointerUp={onPointerUp} onPointerCancel={() => { touchStartX.current = null; }}>
        <img src={activeImage.src} alt={activeImage.alt} draggable={false} className="max-h-[88vh] max-w-full select-none object-contain" onLoad={() => setFailedSrc(null)} onError={() => setFailedSrc(activeImage.src)} />
        {failedSrc === activeImage.src && <p role="status" className="absolute rounded bg-black/90 p-4 text-sm text-white">Unable to load this image.</p>}
        {images.length > 1 && <>
          <button type="button" onClick={() => move(-1)} aria-label="Previous image" className="absolute left-0 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-navy shadow sm:left-3"><ChevronLeft className="h-6 w-6" /></button>
          <button type="button" onClick={() => move(1)} aria-label="Next image" className="absolute right-0 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-navy shadow sm:right-3"><ChevronRight className="h-6 w-6" /></button>
          <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/65 px-3 py-1 text-sm font-semibold text-white">{safeIndex + 1} / {images.length}</span>
        </>}
      </div>
    </div>
  );
}
